import { useMutation, useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import { useState } from 'react';

import {
  API_CUSTOM_SEGMENTS_RESOLVE_EXTERNAL_URL,
  API_SYNDICATED_SEGMENTS_FACETS_URL,
  API_SYNDICATED_SEGMENTS_URL,
} from '../constants/apiUrls';
import { getRequestJSON } from '../util/services';
import { useConfig } from './useConfig';

// Mirrors Admin::SyndicatedSegmentsController#serialize. A row identifies a
// segment by its external identity (external_source + external_id, where
// external_id is the sheet "Segment ID" - im_0001) - NOT by a ventura segment
// id. Use useResolveExternalSegment to turn that pair into a ventura segment.
export interface SyndicatedSegment {
  external_id: string;
  external_source: string;
  segment_name: string;
  segment_desc: string | null;
  segment_size: string | null;
  provider: string | null;
  cpm: string | null;
  geolink_price: string | null;
  category: string | null;
  sub_category_1: string | null;
  sub_category_2: string | null;
  sub_category_3: string | null;
  sub_category_4: string | null;
  sub_category_5: string | null;
  sub_category_6: string | null;
}

export interface ResolvedExternalSegment {
  segment_id: number | string;
  id?: number | string;
  name?: string;
}

export interface SyndicatedSegmentsResponse {
  collection: SyndicatedSegment[];
  pagination: {
    total_items: number;
    per_page: number;
    current_page: number;
  };
}

export interface SyndicatedSegmentFacets {
  sub_category_1: string[];
  sub_category_2: string[];
  sub_category_3: string[];
  sub_category_4: string[];
  sub_category_5: string[];
  sub_category_6: string[];
  providers: string[];
}

export interface SyndicatedSegmentsFilters {
  q?: string;
  provider?: string;
  sub_category_1?: string;
  sub_category_2?: string;
  sub_category_3?: string;
}

function useSyndicatedSegments(filters: SyndicatedSegmentsFilters = {}, pageDefault = 1, perPageDefault = 50) {
  const { apiHost } = useConfig();
  const [page, setPage] = useState<number>(pageDefault);
  const [perPage, setPerPage] = useState<number>(perPageDefault);

  const params: Record<string, string> = {
    page: page.toString(),
    per_page: perPage.toString(),
  };
  if (filters.q) params.q = filters.q;
  if (filters.provider) params.provider = filters.provider;
  if (filters.sub_category_1) params.sub_category_1 = filters.sub_category_1;
  if (filters.sub_category_2) params.sub_category_2 = filters.sub_category_2;
  if (filters.sub_category_3) params.sub_category_3 = filters.sub_category_3;

  const query = useQuery<SyndicatedSegmentsResponse>({
    queryKey: ['syndicatedSegments', page, perPage, filters],
    queryFn: () =>
      getRequestJSON(apiHost, `${API_SYNDICATED_SEGMENTS_URL}?${queryString.stringify(params)}`),
  });

  return {
    page,
    setPage,
    perPage,
    setPerPage,
    ...query,
  };
}

// Resolves a syndicated row's external identity to the ventura segment that was
// backfilled with that (external_source, external_id) pair. The syndicated
// metadata table has no ventura segment id of its own, so this round trip is
// what makes the picked row attachable to an ad group.
export function useResolveExternalSegment() {
  const { apiHost } = useConfig();

  return useMutation<ResolvedExternalSegment, Error, { externalSource: string; externalId: string }>({
    mutationFn: ({ externalSource, externalId }) =>
      getRequestJSON(
        apiHost,
        `${API_CUSTOM_SEGMENTS_RESOLVE_EXTERNAL_URL}?${queryString.stringify({
          external_source: externalSource,
          external_id: externalId,
        })}`
      ),
  });
}

export function useSyndicatedSegmentFacets() {
  const { apiHost } = useConfig();

  return useQuery<SyndicatedSegmentFacets>({
    queryKey: ['syndicatedSegmentFacets'],
    queryFn: () => getRequestJSON(apiHost, API_SYNDICATED_SEGMENTS_FACETS_URL),
    staleTime: Infinity,
  });
}

export default useSyndicatedSegments;
